// n8n-chat/src/components/layouts/home/footer.tsx
"use client";

import * as React from "react";

import { Logo } from "./logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const QUICK_LINKS = [
  { label: "Fonctionnalités", href: "/#features" },
  { label: "Comment ça marche", href: "/#how-it-works" },
  { label: "Chat IA", href: "/#ai-chat" },
  { label: "Prix", href: "/#pricing" },
  { label: "FAQ", href: "/#faq" },
  { label: "Avis", href: "/#reviews" },
  { label: "Contact", href: "/#contact" },
] as const;

const SOCIAL_LINKS = [
  { label: "Instagram", href: "#" },
  { label: "Facebook", href: "#" },
  { label: "Discord", href: "#" },
  { label: "X", href: "#" },
] as const;

type NewsletterStatus = "idle" | "loading" | "success" | "error";

function MailIcon(props: Readonly<React.SVGProps<SVGSVGElement>>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

function ArrowRightIcon(props: Readonly<React.SVGProps<SVGSVGElement>>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}

function CheckIcon(props: Readonly<React.SVGProps<SVGSVGElement>>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function AlertIcon(props: Readonly<React.SVGProps<SVGSVGElement>>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4" />
      <path d="M12 16h.01" />
    </svg>
  );
}

function Spinner() {
  return (
    <div
      aria-hidden="true"
      className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
    />
  );
}

function NewsletterForm() {
  const [email, setEmail] = React.useState("");
  const [status, setStatus] = React.useState<NewsletterStatus>("idle");

  const icon = (() => {
    if (status === "loading") return <Spinner />;
    if (status === "success")
      return <CheckIcon className="h-5 w-5 text-green-400" />;
    if (status === "error")
      return <AlertIcon className="h-5 w-5 text-red-400" />;
    return <ArrowRightIcon className="h-5 w-5 text-white" />;
  })();

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;

    setStatus("loading");

    try {
      const res = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!res.ok) {
        setStatus("error");
        return;
      }

      setEmail("");
      setStatus("success");

      globalThis.setTimeout(() => setStatus("idle"), 2500);
    } catch {
      setStatus("error");
      globalThis.setTimeout(() => setStatus("idle"), 2500);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 p-2 backdrop-blur-sm"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10">
        <MailIcon className="h-5 w-5 text-white" />
      </div>

      <Input
        type="email"
        name="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Enter Your Email..."
        className="flex-1 border-none bg-transparent font-sans text-[14px] text-white placeholder:text-white/40 focus-visible:ring-0 focus-visible:ring-offset-0"
        required
      />

      <Button
        type="submit"
        size="icon"
        disabled={status === "loading"}
        className="h-10 w-10 rounded-full bg-transparent hover:bg-white/10 disabled:opacity-60 cursor-pointer"
      >
        {icon}
      </Button>
    </form>
  );
}

export function Footer() {
  return (
    <footer className="relative border-t border-white/10 bg-black">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_top,rgba(103,41,255,0.15)_0%,transparent_50%)]"
      />

      <div className="mx-auto w-full max-w-[1512px] px-6 py-12 lg:px-[60px] lg:pt-[160px] lg:pb-10">
        <div className="flex flex-col items-start justify-center gap-16 lg:flex-row lg:gap-20 lg:flex-wrap">
          <div className="min-w-[160px]">
            <h3 className="mb-6 font-display text-[16px] font-semibold text-white">
              Menu
            </h3>
            <ul className="space-y-3">
              {QUICK_LINKS.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="font-sans text-sm text-white/60 transition-colors hover:text-white"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="min-w-[160px]">
            <h3 className="mb-6 font-display text-[16px] font-semibold text-white">
              Suivez-nous
            </h3>
            <ul className="space-y-3">
              {SOCIAL_LINKS.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="font-sans text-sm text-white/60 transition-colors hover:text-white"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="min-w-[200px]">
            <h3 className="mb-6 font-display text-[16px] font-semibold text-white">
              Contact
            </h3>
            <a
              href="mailto:contact@stack-flow.io"
              className="block font-sans text-sm text-white/60 transition-colors hover:text-white"
            >
              contact@stack-flow.io
            </a>
          </div>

          <div className="min-w-[300px] max-w-[400px]">
            <div className="flex h-full flex-col">
              <div className="mb-8">
                <Logo className="w-full" />
              </div>

              <p className="mb-8 max-w-[400px] font-sans text-sm leading-6 text-white/60">
                Ne laissez pas l&apos;IA à vos concurrents. Recevez nos
                meilleures stratégies pour accélérer votre business et scaler
                intelligemment.
              </p>

              <NewsletterForm />
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 md:flex-row">
          <p className="text-center font-sans text-[12px] text-white/40 md:text-left">
            © 2026 Stack Flow. All rights reserved.
          </p>

          <div className="flex flex-wrap justify-center gap-6 md:justify-end">
            <a
              href="/legal/privacy"
              className="font-sans text-[12px] text-white/40 transition-colors hover:text-white"
            >
              Politique de Confidentialité
            </a>
            <a
              href="/legal/terms"
              className="font-sans text-[12px] text-white/40 transition-colors hover:text-white"
            >
              CGU
            </a>
            <a
              href="/legal/mentions"
              className="font-sans text-[12px] text-white/40 transition-colors hover:text-white"
            >
              Mentions Légales
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
